/*
 * Copyright (c) 2022-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, computed, effect, OnInit, signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import Aura from '@primeuix/themes/aura';
import {definePreset} from '@primeuix/themes';
import {PrimeNG} from 'primeng/config';
import {NavItem} from './layout/header/nav-item';
import {MenuService} from './layout/menu.service';
import {UserService} from './shared/user.service';
import {EsciService} from './service/esci.service';
import {EsciMessageService} from './shared/esci-message.service';
import {ObjectDescription} from './model/objectDescription';
import {catchError, map, of} from 'rxjs';

/** GM primary blue palette - replaces Aura default emerald (green) */
const GM_PRIMARY_PALETTE = {
  50: '#e2f1fc',
  100: '#b9dcfa',
  200: '#8cc7f7',
  300: '#5eb1f3',
  400: '#39a0f1',
  500: '#0691ef',
  600: '#0483e1',
  700: '#0072ce',
  800: '#0060bc',
  900: '#00439d',
  950: '#00439d',
};

const GmAuraPreset = definePreset(Aura, {
  semantic: {
    primary: GM_PRIMARY_PALETTE,
  },
});


@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.less'],
    standalone: false
})
export class AppComponent implements OnInit {

    constructor(private menuService: MenuService,
                private userService: UserService,
                private esciService: EsciService,
                public messageService: EsciMessageService,
                private primeng: PrimeNG) {
        this.primeng.setConfig({
            theme: {
                preset: GmAuraPreset,
                options: {
                    darkModeSelector: false
                }
            },
            ripple: true
        });
    }

    navItems: NavItem[];
    private dynamicMenuLoaded = false;
    private readonly objectDescriptionsSignal = toSignal(this.esciService.getObjectDescriptions(), {
        initialValue: [] as ObjectDescription[]
    });
    private readonly releaseBannerMessageSignal = toSignal(
        this.esciService.getReleaseBannerMessage().pipe(
            map((message: string) => (message || '').trim()),
            catchError(() => of(''))
        ),
        {initialValue: ''}
    );
    private readonly scheduledReleaseBannerMessageSignal = toSignal(
        this.esciService.getScheduledReleaseBannerMessage().pipe(
            map((message: string) => (message || '').trim()),
            catchError(() => of(''))
        ),
        {initialValue: ''}
    );
    readonly configReleaseBannerMessage = computed(() => this.releaseBannerMessageSignal());
    readonly scheduledReleaseBannerMessage = computed(() => this.scheduledReleaseBannerMessageSignal());
    readonly releaseBannerDismissed = signal(false);
    readonly scheduledReleaseBannerDismissed = signal(false);
    readonly scheduledReleaseSeverity = computed<'warn' | 'error'>(() => {
        const lower = this.scheduledReleaseBannerMessage().toLowerCase();
        return lower.includes('currently being upgraded') ? 'error' : 'warn';
    });
    private readonly dynamicMenuEffect = effect(() => {
        const objDescriptions = this.objectDescriptionsSignal();
        if (!objDescriptions?.length || this.dynamicMenuLoaded || !this.navItems?.length) {
            return;
        }
        for (const objDesc of objDescriptions) {
            this.addComponentMenuItem(objDesc);
        }
        // remove any empty root menu items
        for (const menuLabel of ['Reference Data', 'Component Grids']) {
            const menuItem = this.navItems.find(navItem => navItem.label === menuLabel);
            if (menuItem && (!menuItem.items || menuItem.items.length === 0)) {
                this.navItems = this.navItems.filter(navItem => navItem !== menuItem);
            }
        }
        this.dynamicMenuLoaded = true;
        this.navItems = [...this.navItems];
    });

    ngOnInit(): void {
        this.menuService.setUser(this.userService.user);
        this.userService.enableAutomaticSilentRenew();
        this.setUpNavBars();
    }

    private addComponentMenuItem(objDesc: ObjectDescription): void {
        const pathSegments = (objDesc.rootPath || '')
            .split('/')
            .map(pathSegment => pathSegment.trim())
            .filter(pathSegment => pathSegment.length > 0);
        const menuLabel = (objDesc.displayName || '').trim() || (objDesc.className || '').trim();
        const className = (objDesc.className || '').trim();
        if (!menuLabel || !className) {
            console.warn('Skipping dynamic menu item because displayName/className is missing', objDesc);
            return;
        }
        const defaultRootLabel = pathSegments.includes('Reference Data') ? 'Reference Data' : 'Component Grids';
        const defaultRoot = this.navItems.find(navItem => navItem.label === defaultRootLabel);
        if (!defaultRoot) {
            console.error(`Root menu item "${defaultRootLabel}" not found; unable to add dynamic menu item`, objDesc);
            return;
        }
        if (!defaultRoot.items) {
            defaultRoot.items = [];
        }
        let root = null;
        for (const base of pathSegments) {
            if (base === 'Reference Data') {
                root = this.navItems.find(navItem => navItem.label === 'Reference Data');
                continue; // this path segment is not added to the menu because it's defining the root menu item
            }
            if (root == null) {
                root = this.navItems.find(navItem => navItem.label === 'Component Grids');
                if (base === 'Component Catalog') {
                    continue; // this path segment is not added to the menu because it's defining the root menu item
                }
                // If we get here, the rootPath didn't define the expected root menu item,
                // so we put it under "Component Catalog"
            }
            if (!root) {
                console.error(`Root menu item "Component Grids" or "Reference Data" not found, or pathSegments (${pathSegments}) was malformed`);
                return;
            }
            if (!root.items) {
                root.items = [];
            }

            // Look for an existing child with the current label
            let child = root.items?.find(item => item.label === base);
            if (!child) {
                // Create a new node without 'items' if it's the final segment
                child = {
                    key: this.buildDynamicMenuKey(root, base, undefined, root.items.length),
                    label: base,
                    requireAllPermissions: false,
                    permission: ['*'],
                    items: [],
                    parent: root
                };
                root.items.push(child);
                root.items.sort((o1, o2) => (o1.label || '').localeCompare(o2.label || ''));
            }
            root = child;
        }
        if (!root) {
            root = defaultRoot;
        }
        if (!root.items) {
            root.items = [];
        }
        const childNode = {
            key: this.buildDynamicMenuKey(root, menuLabel, `esciComponentGrid-${className}`, root.items.length),
            label: menuLabel,
            requireAllPermissions: false,
            class: 'subClass',
            permission: ['*'],
            parent: root,
            routerLink: ['esciComponentGrid', className],
        };
        root.items.push(childNode);
        root.items.sort((o1, o2) => {
            if (o1.items === undefined && o2.items !== undefined) {
                return -1;
            }
            if (o2.items === undefined && o1.items !== undefined) {
                return 1;
            }
            return (o1.label || '').localeCompare(o2.label || '');
        });
    }

    // Navigation Methods
    private setUpNavBars() {
        this.navItems = this.menuService.getMenuItems() || [];
        this.ensureMenuKeys(this.navItems, 'root');
    }

    private ensureMenuKeys(items: NavItem[], parentKey: string): void {
        items.forEach((item, index) => {
            if (!item.key || item.key.trim().length === 0) {
                item.key = this.buildDynamicMenuKey({key: parentKey} as NavItem, item.label, this.routeKey(item.routerLink), index);
            }
            if (item.items && item.items.length > 0) {
                this.ensureMenuKeys(item.items, item.key);
            }
        });
    }

    private buildDynamicMenuKey(parent: NavItem, label: string, routeKey: string | undefined, index: number): string {
        const parentKey = parent?.key || 'root';
        const labelPart = (label || 'item').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        const routePart = (routeKey || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        const suffix = routePart ? `${labelPart}-${routePart}` : labelPart;
        return `${parentKey}-${suffix || 'item'}-${index}`;
    }

    private routeKey(routerLink: unknown): string | undefined {
        if (Array.isArray(routerLink)) {
            return routerLink.join('-');
        }
        if (typeof routerLink === 'string') {
            return routerLink;
        }
        return undefined;
    }
}
